import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { CheckIcon, ChevronDownIcon } from '../icons';
import { MENU_ROW, MENU_SURFACE } from '../../styles/menu';
import {
  MENU_ZOOM_VAR,
  menuLayer,
  syncMenuLayer,
} from '../recents/menu-position';

/**
 * The settings dialog's dropdown.
 *
 * A native "<select>" styles its closed box and nothing else: the OPEN list is
 * drawn by the operating system, so it arrived as a light, system-font popup
 * over the dark dialog, with a native scrollbar and a blue highlight that
 * belongs to no theme. There is no cross-browser way to style it.
 *
 * So this is built on the same Radix "DropdownMenu" the composer's model
 * picker uses, and deliberately borrows that component's visual language for
 * the panel (radius, border, elevation, row height, thin scrollbar) — the two
 * places you pick a model in this app should not look like different products.
 *
 * "RadioItem" rather than plain "Item", because this is a one-of-N VALUE, not
 * a command: the role carries "aria-checked", so a screen reader announces
 * which option is current without the check glyph having to mean anything.
 */
export interface SelectOption<T extends string> {
  readonly value: T;
  readonly label: string;
  /** Per-row style — used to preview a font in its own face. */
  readonly style?: CSSProperties;
}

interface Props<T extends string> {
  readonly value: T;
  readonly options: readonly SelectOption<T>[];
  readonly onChange: (value: T) => void;
  /** Accessible name; the trigger is a button, so it needs one. */
  readonly label: string;
  readonly disabled?: boolean;
  /** Style for the trigger's label (the font preview mirrors the selection). */
  readonly triggerStyle?: CSSProperties;
  /** Shown when "value" matches no option — e.g. a stored id since retired. */
  readonly placeholder?: string;
  /** The dialog this select belongs to, stamped onto its portaled panel so the
   *  dialog can tell its own menu from one behind its scrim. */
  readonly ownerId?: string;
}

export function SettingsSelect<T extends string>({
  value,
  options,
  onChange,
  label,
  disabled = false,
  triggerStyle,
  placeholder,
  ownerId,
}: Props<T>) {
  const selected = options.find((o) => o.value === value);

  return (
    <DropdownMenuPrimitive.Root
      onOpenChange={(open) => {
        // The factor can have changed since this rendered — the host writes
        // `zoom` onto an element, which React never sees.
        if (open) syncMenuLayer();
      }}
    >
      <Trigger
        aria-label={label}
        disabled={disabled}
        style={triggerStyle}
        type="button"
      >
        <TriggerLabel>{selected?.label ?? placeholder ?? value}</TriggerLabel>
        <Caret />
      </Trigger>
      <DropdownMenuPrimitive.Portal container={menuLayer()}>
        {/*
          "sideOffset" clears the trigger's border; "collisionPadding" keeps the
          panel off the dialog's edge when the row sits near the bottom.
        */}
        {/*
          "data-settings-menu" is how the dialog recognises ITS OWN open menu.
          Escape has to dismiss one layer at a time, and Radix portals every
          menu in the app to document.body — so a generic
          "[data-radix-menu-content]" probe also saw a picker still open in the
          chat behind the backdrop and left the dialog unable to close.
        */}
        <Content
          data-settings-menu
          data-settings-select-menu={ownerId ?? ''}
          sideOffset={6}
          align="end"
          collisionPadding={12}
        >
          <DropdownMenuPrimitive.RadioGroup
            value={value}
            onValueChange={(next) => onChange(next as T)}
          >
            {options.map((option) => (
              <Item key={option.value} value={option.value}>
                <ItemLabel style={option.style}>{option.label}</ItemLabel>
                <CheckSlot>
                  {option.value === value ? <CheckIcon /> : null}
                </CheckSlot>
              </Item>
            ))}
          </DropdownMenuPrimitive.RadioGroup>
        </Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

/* Matches the old native control's box exactly, so swapping the mechanism
   changed the open list and nothing else about the row's layout. */
const Trigger = styled(DropdownMenuPrimitive.Trigger)`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
  min-width: 220px;
  max-width: 100%;
  height: 32px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: var(--input-bg-color);
  color: var(--text-primary);
  font-family: inherit;
  /* Matches the dialog's body size — see the TYPE scale in SettingsView. */
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;

  &[data-disabled] {
    cursor: default;
    opacity: 0.6;
  }

  &:hover:not([data-disabled]),
  &[data-state='open'] {
    border-color: var(--text-label);
  }

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: 2px;
  }
`;

const TriggerLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Caret = styled(ChevronDownIcon)`
  color: var(--text-label);
`;

const Content = styled(DropdownMenuPrimitive.Content)`
  ${MENU_SURFACE}
  /* Scaled back up by the layer that cancelled the ambient zoom so Radix's
     transform could land where it says — see "menuLayer". */
  zoom: var(${MENU_ZOOM_VAR}, 1);
  /* Never narrower than the trigger it drops from, so the panel reads as the
     field opening rather than as an unrelated popup. */
  /*
    Radix's measurements arrive in SCREEN pixels — it reads the trigger and the
    viewport with "getBoundingClientRect" — and this panel carries the zoom, so
    using them raw scales them a second time: the panel came out ~10% wider
    than its trigger at 110% and ~10% narrower at 90%. Dividing converts them
    into this element's own space, the same correction "toLocalPosition" makes
    for the coordinates.
  */
  min-width: calc(
    var(--radix-dropdown-menu-trigger-width) / var(${MENU_ZOOM_VAR}, 1)
  );
  max-height: calc(
    var(--radix-dropdown-menu-content-available-height) /
      var(${MENU_ZOOM_VAR}, 1)
  );
  /* Above the settings dialog, which sits at 1000. */
  z-index: 2000;
`;

const Item = styled(DropdownMenuPrimitive.RadioItem)`
  ${MENU_ROW}
  /* A fixed trailing column for the check, so labels of different lengths keep
     their ticks in one vertical line rather than ragged against the text. */
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
`;

const ItemLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CheckSlot = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent-color);
`;
